import {
  HeartIcon,
  IdentificationIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { memo, useMemo } from 'react';

import type { ReadPersonWithRelationships } from '@/services/api/partiesService';
import { enhancedApi } from '@/services/api/partiesService';

import { createEntityLink } from '../core/createEntityLink';
import type { EntityTooltipContentProps } from '../core/types';
import {
  EmptyState,
  ErrorState,
  InfoRow,
  LoadingState,
  TooltipHeader,
  TooltipSection,
} from '../primitives';

export type Person = ReadPersonWithRelationships;

export const PersonTooltipContent = memo<EntityTooltipContentProps<Person>>(
  ({ fetchState, isPersistent }) => {
    const person = fetchState.status === 'success' ? fetchState.data : null;

    const fullName = useMemo(() => {
      if (!person) return null;
      return (
        [person.first_name, person.middle_name, person.last_name, person.suffix]
          .filter(Boolean)
          .join(' ') ||
        person.short_description ||
        'Unnamed'
      );
    }, [person]);

    const formattedDOB = useMemo(() => {
      if (!person?.date_of_birth) return null;
      return new Date(person.date_of_birth).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }, [person?.date_of_birth]);

    if (fetchState.status === 'pending') {
      return <LoadingState rows={2} />;
    }

    if (fetchState.status === 'error') {
      return <ErrorState error={fetchState.error} entityType="person" />;
    }

    if (fetchState.status === 'idle' || !person) {
      return <EmptyState entityType="person" />;
    }

    const driversLicense = person.drivers_license_number && (
      <>
        {person.drivers_license_number}
        {person.drivers_license_state_code &&
          ` (${person.drivers_license_state_code})`}
      </>
    );

    const hasRelationships =
      person.personal_relationships && person.personal_relationships.length > 0;

    return (
      <div className="divide-y divide-gray-100">
        <TooltipHeader icon={UserIcon} title={fullName || 'Person'} />

        {(formattedDOB || driversLicense || person.ssn) && (
          <TooltipSection withBorder>
            <InfoRow icon={UserIcon} label="DOB" value={formattedDOB} />
            <InfoRow
              icon={IdentificationIcon}
              label="DL"
              value={driversLicense}
            />
            <InfoRow icon={IdentificationIcon} label="SSN" value={person.ssn} />
          </TooltipSection>
        )}

        {hasRelationships && (
          <TooltipSection withBorder>
            <div className="space-y-1">
              {person.personal_relationships!.map((rel) => {
                const relatedName = [
                  rel.related_person.first_name,
                  rel.related_person.last_name,
                ]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <InfoRow
                    key={rel.id}
                    icon={HeartIcon}
                    label={rel.relationship_type.replace(/_/g, ' ')}
                    value={relatedName || rel.related_person.short_description}
                  />
                );
              })}
            </div>
          </TooltipSection>
        )}

        {person.involvement_description && (
          <TooltipSection withBorder>
            <p className="text-sm text-gray-600">
              {person.involvement_description}
            </p>
          </TooltipSection>
        )}
      </div>
    );
  },
);

PersonTooltipContent.displayName = 'PersonTooltipContent';

export const PersonLink = createEntityLink<
  Person,
  { id: string; matterId: string }
>({
  tooltipContent: PersonTooltipContent,
  useQuery: enhancedApi.endpoints.personsWithRelationships.useQuery,
  getQueryArg: (id) => {
    const [matterId, personId] = id.split(':');
    return { id: personId || id, matterId: matterId || id };
  },
  displayName: 'PersonLink',
});
